import { createBrowserRouter, RouterProvider } from "react-router";
import { lazy, Suspense } from "react";
import * as Sentry from "@sentry/react";

// Lazy load all route components
const Landing = lazy(() => import("./routes/landing"));
const NotFound = lazy(() => import("./routes/not-found"));
const AppLayout = lazy(() => import("./routes/app-layout"));
const Posts = lazy(() => import("./routes/app/posts"));
const UpcomingPostsPage = lazy(() => import("./routes/app/posts/upcoming"));
const PublishedPostsPage = lazy(() => import("./routes/app/posts/published"));
const PendingPostsPage = lazy(() => import("./routes/app/posts/pending"));
const PostDetailPage = lazy(() => import("./routes/app/posts/post-detail"));
const AccountSettings = lazy(() => import("./routes/app/account-settings"));
const SignIn = lazy(() => import("./routes/auth/sign-in"));
const Home = lazy(() => import("./routes/app/home"));
const SSOCallback = lazy(() => import("./routes/auth/sso-callback"));
const Properties = lazy(() => import("./routes/app/properties"));
const Products = lazy(() => import("./routes/app/products"));
const ProductDetail = lazy(() => import("./routes/app/product-detail"));
const Reviews = lazy(() => import("./routes/app/reviews"));
const LogOut = lazy(() => import("./routes/auth/log-out"));
const SetNewPassword = lazy(() => import("./routes/auth/set-new-password"));
const Users = lazy(() => import("./routes/app/users"));
const EditLandlord = lazy(() => import("./routes/app/edit-landlord"));
const PostingSettings = lazy(() => import("./routes/app/posting-settings"));
const PromptTesting = lazy(() => import("./routes/app/prompt-testing"));
const PromptTestingDetail = lazy(() => import("./routes/app/prompt-testing-detail"));
const AdminTools = lazy(() => import("./routes/app/admin-tools"));
const BulkRegeneration = lazy(() => import("./routes/app/bulk-regeneration"));
const BulkRegenerationDetail = lazy(() => import("./routes/app/bulk-regeneration-detail"));
const FourWeekSchedulerMigration = lazy(() => import("./routes/app/four-week-scheduler-migration"));

import { ProtectedRoute } from "@/app/routes/protected-route";
import ForgotPassword from "@/app/routes/auth/forgot-password";
import { ErrorBoundary } from "@/app/components/error-boundary";
import { AdminRoute } from "@/app/routes/admin-route";

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

// Wrap component with Suspense
const withSuspense = (Component: React.ComponentType) => (
  <Suspense>
    <Component />
  </Suspense>
);

export const AppRouter = () => {
  const router = sentryCreateBrowserRouter([
    {
      path: "/",
      element: withSuspense(Landing),
      errorElement: <ErrorBoundary />
    },
    {
      path: "/auth/sign-in",
      element: withSuspense(SignIn),
      errorElement: <ErrorBoundary />
    },
    {
      path: "/auth/forgot-password",
      element: withSuspense(ForgotPassword),
      errorElement: <ErrorBoundary />
    },
    {
      path: "/auth/set-new-password",
      element: withSuspense(SetNewPassword),
      errorElement: <ErrorBoundary />
    },
    {
      path: "/auth/logout",
      element: withSuspense(LogOut),
      errorElement: <ErrorBoundary />
    },
    {
      path: "/auth/sso-callback",
      element: withSuspense(SSOCallback),
      errorElement: <ErrorBoundary />
    },
    {
      path: "/app/:landlordId",
      element: (
        <ProtectedRoute>
          <Suspense>
            <AppLayout />
          </Suspense>
        </ProtectedRoute>
      ),
      errorElement: <ErrorBoundary />,
      children: [
        {
          index: true,
          element: withSuspense(Home)
        },
        {
          path: "posts",
          element: withSuspense(Posts),
          children: [
            {
              index: true,
              element: withSuspense(UpcomingPostsPage)
            },
            {
              path: "upcoming",
              element: withSuspense(UpcomingPostsPage)
            },
            {
              path: "published",
              element: withSuspense(PublishedPostsPage)
            },
            {
              path: "pending",
              element: withSuspense(PendingPostsPage)
            }
          ]
        },
        {
          path: "posts/:postId",
          element: <AdminRoute>{withSuspense(PostDetailPage)}</AdminRoute>
        },
        {
          path: "properties",
          element: withSuspense(Properties)
        },
        {
          path: "products",
          element: withSuspense(Products)
        },
        {
          path: "products/:productId",
          element: withSuspense(ProductDetail)
        },
        {
          path: "posting-settings",
          element: <AdminRoute>{withSuspense(PostingSettings)}</AdminRoute>
        },
        {
          path: "admin-tools",
          element: <AdminRoute>{withSuspense(AdminTools)}</AdminRoute>
        },
        {
          path: "reviews",
          element: withSuspense(Reviews)
        },
        {
          path: "account-settings",
          element: withSuspense(AccountSettings)
        },
        {
          path: "users",
          element: <AdminRoute>{withSuspense(Users)}</AdminRoute>
        },
        {
          path: "edit-landlord",
          element: withSuspense(EditLandlord)
        },
        {
          path: "prompt-testing",
          element: <AdminRoute>{withSuspense(PromptTesting)}</AdminRoute>
        },
        {
          path: "prompt-testing/:testRunId",
          element: <AdminRoute>{withSuspense(PromptTestingDetail)}</AdminRoute>
        },
        {
          path: "bulk-regeneration",
          element: <AdminRoute>{withSuspense(BulkRegeneration)}</AdminRoute>
        },
        {
          path: "bulk-regeneration/:regenerationId",
          element: <AdminRoute>{withSuspense(BulkRegenerationDetail)}</AdminRoute>
        },
        {
          path: "four-week-scheduler-migration",
          element: <AdminRoute>{withSuspense(FourWeekSchedulerMigration)}</AdminRoute>
        }
      ]
    },
    {
      path: "*",
      element: withSuspense(NotFound)
    }
  ]);

  return <RouterProvider router={router} />;
};
